import { z } from 'zod';
export const DashboardKpiSchema = z.object({
    billingThisMonthCents: z.number().int(),
    billingThisYearCents: z.number().int(),
    paidThisYearCents: z.number().int(),
    pendingTotalCents: z.number().int(),
    overduePendingCents: z.number().int(),
    activeOpportunitiesCount: z.number().int(),
    openTasksCount: z.number().int(),
    myOpenTasksCount: z.number().int(),
    unbilledTimeMinutes: z.number().int(),
    unbilledTimeAmountCents: z.number().int(),
    fundsAvailableTotalCents: z.number().int(),
});
export const MonthlyBillingPointSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    invoicesCount: z.number().int(),
});
export const MonthlyBillingResponseSchema = z.object({
    points: z.array(MonthlyBillingPointSchema),
});
export const BreakdownPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
});
export const OpportunitiesBreakdownResponseSchema = z.object({
    byStage: z.array(BreakdownPointSchema),
    byServiceLine: z.array(BreakdownPointSchema),
});
export const TopClientItemSchema = z.object({
    clientId: z.string(),
    clientDisplayName: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    invoicesCount: z.number().int(),
});
export const TopClientsResponseSchema = z.object({
    year: z.number().int(),
    items: z.array(TopClientItemSchema),
});
// Forecast de facturación a fin de año: combina facturación real, hitos
// planificados, ingresos recurrentes proyectados y pipeline ponderado.
export const ForecastMonthSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    /** Facturación real (Invoice EMITIDA/COBRADA/PARCIAL) del mes. */
    invoicedCents: z.number().int(),
    /** Hitos de pago PENDIENTE con dueDate en el mes. */
    milestonesCents: z.number().int(),
    /** Suma del pipeline ponderado por probabilidad de cierre. */
    pipelineExpectedCents: z.number().int(),
    /** Recurrencias activas proyectadas al mes. */
    recurringCents: z.number().int(),
    /** Suma de los 4 componentes anteriores. */
    totalExpectedCents: z.number().int(),
});
export const ForecastResponseSchema = z.object({
    year: z.number().int(),
    /** 12 meses (1-12) en orden cronológico. */
    months: z.array(ForecastMonthSchema),
    totalYearCents: z.number().int(),
});
// ---------------------------------------------------------------------------
// Dashboard overview (rediseño): agregados por área para un periodo
// (año / trimestre / mes). Un solo endpoint alimenta las 4 áreas.
// ---------------------------------------------------------------------------
/** Punto de desglose con nº de elementos + importe asociado (céntimos). */
export const AmountBreakdownPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
    amountCents: z.number().int(),
});
/** Serie mensual con dos magnitudes (p.ej. puntual vs recurrente). */
export const MonthSeriesPointSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    primaryCents: z.number().int(),
    secondaryCents: z.number().int(),
});
/** Finanzas por proyecto: vendido / facturado / cobrado. */
export const ProjectFinancePointSchema = z.object({
    opportunityId: z.string(),
    label: z.string(),
    soldCents: z.number().int(),
    billedCents: z.number().int(),
    collectedCents: z.number().int(),
});
/** Venta por advisor: importe vendido + comisión. */
export const AdvisorSalesPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    soldCents: z.number().int(),
    commissionCents: z.number().int(),
});
/** Tarea abierta resumida para el listado del panel. */
export const DashboardOpenTaskSchema = z.object({
    id: z.string(),
    title: z.string(),
    dueAt: z.string().datetime().nullable(),
    priority: z.string(),
});
/**
 * Las cifras económicas del panel, con definiciones precisas (acuerdo del 22-10-2026). Todo en
 * céntimos. «Del periodo» obedece al filtro del panel; «a hoy» es un saldo y NO obedece al filtro.
 */
export const EconomicsDetailSchema = z.object({
    /** Facturado en el periodo: base imponible (lo que se abona a la 70501), IVA y total a cobrar. */
    billed: z.object({
        baseCents: z.number().int(),
        vatCents: z.number().int(),
        totalCents: z.number().int(),
    }),
    /** Gasto REAL del periodo (sin la inversión comprometida, que es una estimación): base, IVA y total. */
    spent: z.object({
        baseCents: z.number().int(),
        vatCents: z.number().int(),
        totalCents: z.number().int(),
    }),
    /**
     * Pendiente de EMITIR, a hoy. El titular exacto es `drafts` + `milestonesDue` + `recurringDue`; lo `planned` es
     * futuro y NO entra en el titular.
     */
    pendingIssue: z.object({
        /** Borradores de factura ordinaria vivos (importes reales), con su origen. */
        drafts: z.object({
            count: z.number().int(),
            baseCents: z.number().int(),
            totalCents: z.number().int(),
            byOrigin: z.object({
                milestone: z.object({ count: z.number().int(), baseCents: z.number().int() }),
                recurring: z.object({ count: z.number().int(), baseCents: z.number().int() }),
                manual: z.object({ count: z.number().int(), baseCents: z.number().int() }),
            }),
        }),
        /** Hitos pendientes ya vencidos y sin facturar (base imponible): salen en Facturación → Pendientes. */
        milestonesDue: z.object({ count: z.number().int(), baseCents: z.number().int() }),
        /**
         * Cuotas de pagos recurrentes que ya han llegado a su fecha y no se han emitido (base imponible). Desde el 26-10-2026 ya
         * no se convierten en borrador por la noche, así que sin esta cifra no contarían en ninguna.
         */
        recurringDue: z
            .object({ count: z.number().int(), baseCents: z.number().int() })
            .default({ count: 0, baseCents: 0 }),
        /** Hitos pendientes con vencimiento futuro: planificado, no pendiente. */
        milestonesPlanned: z.object({ count: z.number().int(), baseCents: z.number().int() }),
    }),
    /** Pendiente de COBRO, a hoy: saldo real de las facturas emitidas, sin filtro de fechas. */
    pendingCollect: z.object({
        totalCents: z.number().int(),
        overdueCents: z.number().int(),
        count: z.number().int(),
        /** Lo pendiente de lo facturado EN el periodo (la cifra antigua, como pista secundaria). */
        ofPeriodCents: z.number().int(),
    }),
    /** Pendiente de pagar a proveedores, a hoy (cuadra con la 400xxxxx). */
    pendingPay: z.object({ totalCents: z.number().int(), count: z.number().int() }),
});
/** Una cifra fiscal y de dónde sale: EXACT = del libro, ESTIMATED = una estimación rotulada. */
const TaxKindSchema = z.enum(['EXACT', 'ESTIMATED']);
export const TaxPositionSchema = z.object({
    asOf: z.string(),
    period: z.object({ from: z.string(), to: z.string(), label: z.string() }),
    vat: z.object({
        /** IVA repercutido y soportado DEL PERIODO (sin el asiento de liquidación) y su diferencia. */
        generated: z.object({
            repercutidoCents: z.number().int(),
            soportadoCents: z.number().int(),
            netCents: z.number().int(),
            kind: TaxKindSchema,
        }),
        /** 47500 a hoy: liquidado y pendiente de pagar. */
        payableCents: z.number().int(),
        /** 47001 a hoy: a compensar o devolver (NO se resta en silencio). */
        receivableCents: z.number().int(),
        /** Saldo vivo 477 − 472 a hoy: el trimestre en curso, aún sin liquidar. */
        openQuarterCents: z.number().int(),
        nextDeadline: z.object({ date: z.string(), label: z.string() }),
    }),
    irpf: z.object({
        /** Retenciones practicadas en el periodo (los gastos con IRPF). */
        generatedCents: z.number().int(),
        /** 47501 + 4752 a hoy, saldo acreedor (lo que se debe ingresar). Nunca negativo. */
        payableCents: z.number().int(),
        /** Si la 47501 queda deudora (retenciones que le han hecho a JR2): a favor, aparte. */
        receivableCents: z.number().int(),
        kind: TaxKindSchema,
    }),
    corporateTax: z.object({
        /** Lo contabilizado: gasto del periodo (63000), a pagar (47502) y pagado a cuenta (47300). */
        booked: z.object({
            expenseCents: z.number().int(),
            payableCents: z.number().int(),
            paidOnAccountCents: z.number().int(),
        }),
        /** Estimación, rotulada: sin ajustes fiscales ni compensación de bases, y sin tipo confirmado. */
        estimate: z.object({
            kind: z.literal('ESTIMATED'),
            /** Resultado antes de impuestos del año hasta hoy (suelo 0). */
            baseCents: z.number().int(),
            lowRatePct: z.number(),
            highRatePct: z.number(),
            lowCents: z.number().int(),
            highCents: z.number().int(),
        }),
    }),
    /** 47500 + 47501 + 4752 + 47502 + el trimestre de IVA en curso (si es a pagar): lo que se debe a Hacienda según el libro a hoy. Los saldos a favor no se restan. */
    totalPendingCents: z.number().int(),
});
export const DashboardOverviewResponseSchema = z.object({
    period: z.object({
        year: z.number().int(),
        quarter: z.number().int().nullable(),
        month: z.number().int().nullable(),
        label: z.string(),
    }),
    opportunities: z.object({
        newCount: z.number().int(),
        sentCount: z.number().int(),
        lostCount: z.number().int(),
        wonCount: z.number().int(),
        byStage: z.array(AmountBreakdownPointSchema),
        byServiceLine: z.array(AmountBreakdownPointSchema),
        byAdvisor: z.array(AmountBreakdownPointSchema),
    }),
    projects: z.object({
        onboardingCount: z.number().int(),
        executionCount: z.number().int(),
        supportCount: z.number().int(),
        finishedCount: z.number().int(),
        byPhase: z.array(AmountBreakdownPointSchema),
        byRoadmap: z.array(AmountBreakdownPointSchema),
        byMilestone: z.array(AmountBreakdownPointSchema),
        finance: z.array(ProjectFinancePointSchema),
    }),
    economics: z.object({
        billedCents: z.number().int(),
        spentCents: z.number().int(),
        /** Parte de spentCents que proviene de oportunidades En Inversión. */
        investedCents: z.number().int(),
        pendingIssueCents: z.number().int(),
        pendingCollectCents: z.number().int(),
        /** Las mismas cifras con definiciones precisas (base imponible, saldos a hoy, desglose). */
        detail: EconomicsDetailSchema,
        billingByServiceLine: z.array(AmountBreakdownPointSchema),
        billingByClient: z.array(AmountBreakdownPointSchema),
        expenseByCategory: z.array(AmountBreakdownPointSchema),
        /** 12 meses del año: primary = puntual (hitos), secondary = recurrente. */
        salesByMonth: z.array(MonthSeriesPointSchema),
        /** Venta por advisor (oportunidades ganadas del periodo): vendido + comisión. */
        advisorSales: z.array(AdvisorSalesPointSchema),
        topClients: z.array(TopClientItemSchema),
    }),
    activities: z.object({
        openTasksCount: z.number().int(),
        myOpenTasksCount: z.number().int(),
        upcomingEventsCount: z.number().int(),
        /** Listado corto de tareas abiertas (para el panel). */
        openTasks: z.array(DashboardOpenTaskSchema),
    }),
});
//# sourceMappingURL=stats.js.map